import { Deferred } from '@duda-co/async';
import { logService } from '@duda-co/host-services';
import rison from 'rison';
import { PROVIDERS } from '../constants/consts';
import { isCollectionsJSapiFilterValidationEnabled } from '../services/flagService';
import {
    validateLegacyFilter,
    validatePredicateClause,
    validateQueryFilters,
} from './filterQueryValidation';

const LOGICAL_OPERATORS = {
    AND: 'AND',
    OR: 'OR',
};

/**
 * Beta / preview toggle that enables the flexible where (grouped AND/OR) query builder.
 * Once enabled, it remains active until the page reloads.
 * @deprecated This function is no longer needed as flexible querying is always enabled.
 */
export function enableFlexibleQueryPreview() {
    console.warn(
        '[Collection API] enableFlexibleQueryPreview() is deprecated and no longer needed. Flexible querying is now always enabled.'
    );
}

if (typeof window.enableFlexibleQueryPreview !== 'function') {
    /**
     * Beta / preview toggle exposed globally for seamless testing.
     */
    window.enableFlexibleQueryPreview = enableFlexibleQueryPreview;
}

/**
 * Beta / preview toggle that enables the aggregate API.
 * @deprecated This function is no longer needed as aggregate API is always enabled.
 */
export function enableAggregatePreview() {
    console.warn(
        '[Collection API] enableAggregatePreview() is deprecated and no longer needed. Aggregate API is now always enabled.'
    );
    // No-op: aggregate API is always enabled now
}

if (typeof window.enableAggregatePreview !== 'function') {
    /**
     * Beta / preview toggle exposed globally for seamless testing.
     */
    window.enableAggregatePreview = enableAggregatePreview;
}

function pathParamsForCurrentServer() {
    const isRuntime = window.Parameters.isRuntimeServer;
    const prefix = isRuntime ? 'rts' : 'ms';
    const siteFlow = isRuntime ? 'runtime' : 'designtime';
    return { prefix, siteFlow, siteAlias: window.Parameters.SiteAlias };
}

function buildUrlWithParams(collection, func, params) {
    const valueToString = (val) => {
        const stringVal = typeof val === 'string' ? val : JSON.stringify(val);
        return sanitizeStringValue(stringVal);
    };
    params = Object.entries(params)
        .filter(([_, val]) => val !== undefined && val !== null && val !== '')
        .flatMap(([param, val]) => {
            // Sanitize filter values before converting to string
            const sanitizedVal = Array.isArray(val)
                ? val.map((elem) => sanitizeFilterValue(elem))
                : sanitizeFilterValue(val);
            return Array.isArray(sanitizedVal)
                ? sanitizedVal.map((elem) => [param, valueToString(elem)])
                : [[param, valueToString(sanitizedVal)]];
        });
    const query = new URLSearchParams(params);
    if (window.currentLanguage) {
        query.set('language', window.currentLanguage);
    }

    // Pound symbol (#) is not "acting nicely" when its part of the path. (We have routing issue with it and its not getting to runtime-cache)
    // It mainly effect this path when querying store-catalog, so quick hacky solution is to swap # with __ before querying the collection and then swap back on server.

    const storeCatalogCollections = [
        'internal-usage#store_catalog#catalog_product',
        'internal-usage#store_catalog#catalog_category',
        'internal-usage#store_catalog#catalog_customization',
    ];

    if (storeCatalogCollections.includes(collection)) {
        collection = collection.replaceAll('#', '__');
    }

    const encodedName = encodeURIComponent(collection);
    const path = pathParamsForCurrentServer();
    const url = `/${path.prefix}/collections/public/${path.siteAlias}/${path.siteFlow}/collection/${encodedName}/${func}`;
    return query.toString() ? `${url}?${query}` : url;
}

const cache = new Map();

async function cacheableRequest(url, { context }) {
    if (cache.has(url)) {
        return cache.get(url).promise;
    }

    const deferred = new Deferred();

    cache.set(url, deferred);

    let response;
    try {
        response = await fetch(url);

        const result = isJsonResponseCompatible(response)
            ? await response?.json()
            : await response?.text();

        if (!response.ok) {
            throw new Error(result?.message || result);
        }

        deferred.resolve(result);

        return result;
    } catch (error) {
        deferred.resolve(error);
        logError({ error, context, response });
        throw error;
    }
}

export async function getCachedResponses() {
    let result = {};
    for (let [url, deferred] of cache) {
        result[url] = await deferred.promise;
    }
    return result;
}

export function clearCache() {
    cache.clear();
}

/**
 * Fetches collection data based on the provided query parameters.
 * Supports both legacy filters and flexible where clauses (when enabled).
 *
 * @param {Object} params - Query parameters
 * @param {string} params.name - Collection name
 * @param {Object} [params.page] - Pagination object
 * @param {number} [params.page.pageSize] - Number of items per page
 * @param {number} [params.page.pageNumber] - Page number (0-indexed)
 * @param {Array<Object>} [params.filters] - Legacy filter array (cannot be used with where)
 * @param {string} params.filters[].field - Field name to filter on
 * @param {string} params.filters[].operator - Operator: 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'btwn'
 * @param {*} params.filters[].value - Filter value
 * @param {string} [params.search] - Search string to filter collection data
 * @param {Array<Object>} [params.sortBy] - Sort configuration array
 * @param {string} params.sortBy[].field - Field name to sort by
 * @param {string} [params.sortBy[].direction] - Sort direction: 'asc' or 'desc'
 * @param {Array<string>} [params.fields] - Array of field names to select/return
 * @param {Object} [params.where] - Flexible where clause (requires enableFlexibleQueryPreview())
 * @param {string} params.where.op - Logical operator: 'AND' or 'OR'
 * @param {Array} params.where.clauses - Array of predicate clauses or nested groups
 * @returns {Promise<Object>} Collection data response
 *
 * @example
 * // Basic usage - fetch all data from a collection
 * const data = await getCollectionData({ name: 'products' });
 *
 * @example
 * // With pagination
 * const data = await getCollectionData({
 *   name: 'products',
 *   page: { pageSize: 20, pageNumber: 0 }
 * });
 *
 * @example
 * // With legacy filters
 * const data = await getCollectionData({
 *   name: 'products',
 *   filters: [
 *     { field: 'category', operator: 'eq', value: 'Electronics' },
 *     { field: 'price', operator: 'gte', value: 100 }
 *   ]
 * });
 *
 * @example
 * // With search
 * const data = await getCollectionData({
 *   name: 'products',
 *   search: 'laptop'
 * });
 *
 * @example
 * // With sorting and field selection
 * const data = await getCollectionData({
 *   name: 'products',
 *   sortBy: [{ field: 'price', direction: 'desc' }],
 *   fields: ['name', 'price', 'category']
 * });
 *
 * @example
 * // With flexible where clause (requires enableFlexibleQueryPreview())
 * enableFlexibleQueryPreview();
 * const data = await getCollectionData({
 *   name: 'products',
 *   where: {
 *     op: 'AND',
 *     clauses: [
 *       { fieldName: 'category', operator: 'EQ', value: 'Electronics' },
 *       { fieldName: 'price', operator: 'GTE', value: 100 }
 *     ]
 *   }
 * });
 *
 * @example
 * // Complex query with pagination, search, sorting, and flexible where
 * enableFlexibleQueryPreview();
 * const data = await getCollectionData({
 *   name: 'orders',
 *   page: { pageSize: 50, pageNumber: 1 },
 *   search: 'urgent',
 *   sortBy: [{ field: 'createdAt', direction: 'desc' }],
 *   fields: ['id', 'status', 'total'],
 *   where: {
 *     op: 'OR',
 *     clauses: [
 *       { fieldName: 'status', operator: 'EQ', value: 'pending' },
 *       { fieldName: 'status', operator: 'EQ', value: 'processing' }
 *     ]
 *   }
 * });
 */
export async function getCollectionData(params) {
    const { name, page, filters, search, sortBy, fields, where } = params;

    if (where && filters && filters.length) {
        throw new Error(
            "[Collection API] Invalid query: cannot provide both 'filters' and 'where' when flexible querying is enabled."
        );
    }

    if (isCollectionsJSapiFilterValidationEnabled()) {
        validateQueryFilters({ filters, where });
    }

    const endpoint = 'query-data';
    const queryParams = {
        pageSize: page?.pageSize,
        pageNumber: page?.pageNumber,
        query: encodeQueryPayload({
            filters,
            sortBy,
            search,
            fields,
            where,
        }),
    };

    return await cacheableRequest(
        buildUrlWithParams(name, endpoint, queryParams),
        { context: params }
    );
}

/**
 * Aggregates collection data based on the provided aggregation specifications.
 * This is the raw/low-level API. For a more convenient builder API, use aggregate().
 *
 * @param {Object} params - Aggregation parameters
 * @param {string} params.name - Collection name
 * @param {Object} [params.where] - Where clause for filtering (requires enableAggregatePreview() or enableFlexibleQueryPreview())
 * @param {Array<Object>} params.aggregations - Array of aggregation specifications
 * @param {string} params.aggregations[].id - Unique identifier for the aggregation
 * @param {Array<string>} [params.aggregations[].groupBy] - Fields to group by
 * @param {Array<string>} [params.aggregations[].unwind] - Fields to unwind (for nested arrays)
 * @param {Array<Object>} params.aggregations[].metrics - Array of metric specifications
 * @param {string} params.aggregations[].metrics[].id - Unique identifier for the metric
 * @param {string} params.aggregations[].metrics[].type - Metric type: 'COUNT', 'SUM', 'MIN', 'MAX', 'AVG'
 * @param {string} [params.aggregations[].metrics[].field] - Field name (required for SUM, MIN, MAX, AVG)
 * @param {string} [params.language] - Language code (optional, defaults to window.currentLanguage)
 * @returns {Promise<Object>} Aggregation results
 *
 * @example
 * enableAggregatePreview();
 * await executeAggregation({
 *   name: 'products',
 *   where: {
 *     op: 'AND',
 *     clauses: [{ fieldName: 'category', operator: 'EQ', value: 'Electronics' }]
 *   },
 *   aggregations: [{
 *     id: 'avgPrice',
 *     metrics: [{ id: 'avgPrice', type: 'AVG', field: 'price' }]
 *   }]
 * });
 */
export async function executeAggregation(params) {
    const { name, where, aggregations, language } = params;

    validateAggregateParams(params);
    if (isCollectionsJSapiFilterValidationEnabled()) {
        validateQueryFilters({ where });
    }

    const queryParams = {
        query: encodeQueryPayload({
            where,
            aggregations,
        }),
    };

    // Language is handled by buildUrlWithParams if window.currentLanguage is set
    // But we can also explicitly pass it if provided
    if (language) {
        queryParams.language = language;
    }

    return await cacheableRequest(
        buildUrlWithParams(name, 'aggregate', queryParams),
        { context: params }
    );
}

function validateAggregateParams(params) {
    const { name, aggregations } = params;

    if (!name) {
        throw new Error(
            '[Collection API] Collection name is required for aggregate operation.'
        );
    }

    if (
        !aggregations ||
        !Array.isArray(aggregations) ||
        aggregations.length === 0
    ) {
        throw new Error(
            '[Collection API] At least one aggregation specification is required.'
        );
    }

    // Validate aggregations structure
    aggregations.forEach((agg, index) => {
        if (!agg.id) {
            throw new Error(
                `[Collection API] Aggregation at index ${index} must have an 'id' field.`
            );
        }
        if (
            !agg.metrics ||
            !Array.isArray(agg.metrics) ||
            agg.metrics.length === 0
        ) {
            throw new Error(
                `[Collection API] Aggregation '${agg.id}' must have at least one metric.`
            );
        }
        agg.metrics.forEach((metric, metricIndex) => {
            if (!metric.id) {
                throw new Error(
                    `[Collection API] Metric at index ${metricIndex} in aggregation '${agg.id}' must have an 'id' field.`
                );
            }
            if (!metric.type) {
                throw new Error(
                    `[Collection API] Metric '${metric.id}' in aggregation '${agg.id}' must have a 'type' field.`
                );
            }
            const validTypes = ['COUNT', 'SUM', 'MIN', 'MAX', 'AVG'];
            if (!validTypes.includes(metric.type.toUpperCase())) {
                throw new Error(
                    `[Collection API] Invalid metric type '${
                        metric.type
                    }' in aggregation '${
                        agg.id
                    }'. Valid types: ${validTypes.join(', ')}.`
                );
            }
            const requiresField = ['SUM', 'MIN', 'MAX', 'AVG'];
            if (
                requiresField.includes(metric.type.toUpperCase()) &&
                !metric.field
            ) {
                throw new Error(
                    `[Collection API] Metric '${metric.id}' in aggregation '${agg.id}' with type '${metric.type}' requires a 'field' property.`
                );
            }
        });
    });
}

export function storeCollectionQueryBuilder(name) {
    return commonQueryBuilder(name, PROVIDERS.STORE);
}

export function collectionQueryBuilder(name) {
    return commonQueryBuilder(name, PROVIDERS.USER);
}

/**
 * Creates a predicate builder for a group (used in andGroup/orGroup callbacks).
 */
function createGroupPredicateBuilder(group, methodName) {
    function addPredicateToGroup(field, operator, value, predicateMethodName) {
        group.clauses.push(
            buildPredicateClause(field, operator, value, predicateMethodName)
        );
    }

    const groupApi = {
        where(field, operator, value) {
            addPredicateToGroup(field, operator, value, `${methodName}.where`);
            return groupApi;
        },
    };

    attachPredicateShortcuts(
        groupApi,
        (field, operator, value, shortcut) => {
            addPredicateToGroup(
                field,
                operator,
                value,
                `${methodName}.${shortcut}`
            );
        },
        groupApi
    );

    return groupApi;
}

/**
 * Creates a reusable where builder that can be used in callbacks.
 * This is used internally by aggregate() and commonQueryBuilder().
 */
function createWhereBuilder(
    whereState,
    activateModeFn,
    addPredicateFn,
    getClausesFn,
    methodPrefix
) {
    function root(op = LOGICAL_OPERATORS.AND) {
        activateModeFn(`${methodPrefix}.root`);
        whereState.op = normalizeLogicalOp(op, `${methodPrefix}.root`);
        return whereApi;
    }

    function where(field, operator, value) {
        addPredicateFn(field, operator, value, `${methodPrefix}.where`);
        return whereApi;
    }

    function andGroup(callback) {
        return createWhereGroup(
            LOGICAL_OPERATORS.AND,
            callback,
            `${methodPrefix}.andGroup`
        );
    }

    function orGroup(callback) {
        return createWhereGroup(
            LOGICAL_OPERATORS.OR,
            callback,
            `${methodPrefix}.orGroup`
        );
    }

    function createWhereGroup(op, callback, methodName) {
        if (typeof callback !== 'function') {
            throw new Error(
                `[Collection API] ${methodName} requires a callback function that defines predicates.`
            );
        }
        activateModeFn(methodName);
        const group = {
            op,
            clauses: [],
        };
        getClausesFn().push(group);
        const groupBuilder = createGroupPredicateBuilder(group, methodName);
        callback(groupBuilder);
        if (!group.clauses.length) {
            throw new Error(
                `[Collection API] ${methodName} callback must define at least one predicate.`
            );
        }
        return whereApi;
    }

    const whereApi = {
        root,
        where,
        andGroup,
        orGroup,
    };

    // Attach predicate shortcuts
    attachPredicateShortcuts(
        whereApi,
        (field, operator, value, shortcut) => {
            activateModeFn(`${methodPrefix}.${shortcut}`);
            addPredicateFn(
                field,
                operator,
                value,
                `${methodPrefix}.${shortcut}`
            );
        },
        whereApi
    );

    return whereApi;
}

/**
 * Creates an aggregate query builder for the specified collection.
 * Requires enableAggregatePreview() to be called first (which also enables flexible where clauses).
 *
 * @param {string} name - Collection name
 * @returns {Object} Aggregate query builder API with methods: exec(), agg(), root(), andGroup(), orGroup(), language(), and predicate shortcuts (eq, ne, gt, gte, lt, lte, in, nin, btwn)
 *
 * @example
 * enableAggregatePreview();
 * await aggregate('products')
 *   .root('AND')
 *   .eq('display_status', 'ACTIVE')
 *   .eq('stock_status', 'IN_STOCK')
 *   .andGroup(g => g.in('brand', ['Nike', 'Adidas']).lt('price', 100))
 *   .agg('productOptions', (a) =>
 *     a.unwind('options', 'options.choices_multi_select')
 *      .groupBy('options.name', 'options.choices_multi_select')
 *      .count()
 *   )
 *   .agg('priceRange', (a) =>
 *     a.min('minPrice', 'price')
 *      .max('maxPrice', 'price')
 *   )
 *   .exec();
 */
export function aggregate(name) {
    let flexibleWhereState;
    let flexibleWhereModeActive = false;
    let aggregations = [];
    let language;

    function exec() {
        return executeAggregation({
            name,
            where: flexibleWhereModeActive ? flexibleWhereState : undefined,
            aggregations,
            language,
        });
    }

    function agg(id, callback) {
        if (!id) {
            throw new Error(
                '[Collection API] Aggregation id is required for agg().'
            );
        }
        if (typeof callback !== 'function') {
            throw new Error(
                '[Collection API] agg() requires a callback function that defines the aggregation.'
            );
        }

        const aggregation = {
            id,
            metrics: [],
        };

        const aggregationBuilder = createAggregationBuilder(aggregation);
        callback(aggregationBuilder);

        aggregations.push(aggregation);
        return api;
    }

    function createAggregationBuilder(aggregation) {
        const aggApi = {
            groupBy(...fields) {
                aggregation.groupBy = fields.filter((f) => f != null);
                return aggApi;
            },

            unwind(...fields) {
                aggregation.unwind = fields.filter((f) => f != null);
                return aggApi;
            },

            count(id = 'count') {
                aggregation.metrics.push({ id, type: 'COUNT' });
                return aggApi;
            },

            sum(id, field) {
                if (!field) {
                    throw new Error(
                        '[Collection API] Field is required for sum().'
                    );
                }
                aggregation.metrics.push({ id, type: 'SUM', field });
                return aggApi;
            },

            min(id, field) {
                if (!field) {
                    throw new Error(
                        '[Collection API] Field is required for min().'
                    );
                }
                aggregation.metrics.push({ id, type: 'MIN', field });
                return aggApi;
            },

            max(id, field) {
                if (!field) {
                    throw new Error(
                        '[Collection API] Field is required for max().'
                    );
                }
                aggregation.metrics.push({ id, type: 'MAX', field });
                return aggApi;
            },

            avg(id, field) {
                if (!field) {
                    throw new Error(
                        '[Collection API] Field is required for avg().'
                    );
                }
                aggregation.metrics.push({ id, type: 'AVG', field });
                return aggApi;
            },
        };

        return aggApi;
    }

    function activateFlexibleMode(invokerName) {
        ensureFlexibleFeatureEnabled(invokerName);
        if (!flexibleWhereModeActive) {
            flexibleWhereModeActive = true;
            if (!flexibleWhereState) {
                flexibleWhereState = {
                    op: LOGICAL_OPERATORS.AND,
                    clauses: [],
                };
            }
        }
    }

    function addFlexiblePredicate(field, operator, value, methodName) {
        const clauses = getFlexibleClauses();
        clauses.push(buildPredicateClause(field, operator, value, methodName));
    }

    function getFlexibleClauses() {
        if (!flexibleWhereState) {
            flexibleWhereState = {
                op: LOGICAL_OPERATORS.AND,
                clauses: [],
            };
        } else if (!flexibleWhereState.clauses) {
            flexibleWhereState.clauses = [];
        }
        return flexibleWhereState.clauses;
    }

    // Initialize flexibleWhereState before creating the builder
    if (!flexibleWhereState) {
        flexibleWhereState = {
            op: LOGICAL_OPERATORS.AND,
            clauses: [],
        };
    }

    // Create a shared where builder that both commonQueryBuilder and aggregate use
    const whereBuilder = createWhereBuilder(
        flexibleWhereState,
        activateFlexibleMode,
        addFlexiblePredicate,
        getFlexibleClauses,
        'aggregate'
    );

    // Expose where builder methods directly on the API, but return api for chaining
    function root(op = LOGICAL_OPERATORS.AND) {
        whereBuilder.root(op);
        return api;
    }

    function andGroup(callback) {
        whereBuilder.andGroup(callback);
        return api;
    }

    function orGroup(callback) {
        whereBuilder.orGroup(callback);
        return api;
    }

    function setLanguage(lang) {
        language = lang;
        return api;
    }

    const api = {
        exec,
        agg,
        root,
        andGroup,
        orGroup,
        language: setLanguage,
    };

    // Attach predicate shortcuts that delegate to the where builder
    attachPredicateShortcuts(
        api,
        (field, operator, value, shortcut) => {
            activateFlexibleMode(shortcut);
            addFlexiblePredicate(field, operator, value, shortcut);
        },
        api
    );

    return api;
}

/**
 * Creates a fluent query builder for collection data.
 * This is the internal implementation used by collectionQueryBuilder() and storeCollectionQueryBuilder().
 *
 * @param {string} name - Collection name
 * @param {string} [provider=PROVIDERS.USER] - Collection provider (USER or STORE)
 * @returns {Object} Query builder API with methods: get(), where(), search(), orderBy(), select(), pageSize(), pageNumber(), root(), andGroup(), orGroup(), and predicate shortcuts (eq, ne, gt, gte, lt, lte, in, nin, btwn)
 *
 * @example
 * // Basic usage - fetch all data
 * const data = await collectionQueryBuilder('products').get();
 *
 * @example
 * // With pagination
 * const data = await collectionQueryBuilder('products')
 *   .pageSize(20)
 *   .pageNumber(0)
 *   .get();
 *
 * @example
 * // With legacy filters
 * const data = await collectionQueryBuilder('products')
 *   .where('category', 'eq', 'Electronics')
 *   .where('price', 'gte', 100)
 *   .get();
 *
 * @example
 * // With search
 * const data = await collectionQueryBuilder('products')
 *   .search('laptop')
 *   .get();
 *
 * @example
 * // With sorting and field selection
 * const data = await collectionQueryBuilder('products')
 *   .orderBy('price', 'desc')
 *   .orderBy('name', 'asc')
 *   .select('name', 'price', 'category')
 *   .get();
 *
 * @example
 * // With flexible where clause (requires enableFlexibleQueryPreview())
 * enableFlexibleQueryPreview();
 * const data = await collectionQueryBuilder('products')
 *   .root('AND')
 *   .eq('category', 'Electronics')
 *   .gte('price', 100)
 *   .get();
 *
 * @example
 * // With grouped conditions (requires enableFlexibleQueryPreview())
 * enableFlexibleQueryPreview();
 * const data = await collectionQueryBuilder('orders')
 *   .root('OR')
 *   .eq('status', 'pending')
 *   .andGroup(g => g.gte('total', 100).eq('country', 'US'))
 *   .orGroup(g => g.eq('priority', 'high').lt('createdAt', '2024-01-01'))
 *   .get();
 *
 * @example
 * // Complex query combining all features
 * enableFlexibleQueryPreview();
 * const data = await collectionQueryBuilder('orders')
 *   .search('urgent')
 *   .root('AND')
 *   .eq('status', 'active')
 *   .orGroup(g => g.gte('total', 500).in('paymentMethod', ['VISA', 'MC']))
 *   .orderBy('createdAt', 'desc')
 *   .select('id', 'status', 'total', 'createdAt')
 *   .pageSize(50)
 *   .pageNumber(1)
 *   .get();
 *
 * @example
 * // Store catalog collection
 * const data = await storeCollectionQueryBuilder('catalog_product')
 *   .where('category', 'eq', 'Electronics')
 *   .get();
 */
function commonQueryBuilder(name, provider = PROVIDERS.USER) {
    let filters;
    let searchString;
    let sortBy;
    let page;
    let fields;
    let flexibleWhereState;
    let flexibleWhereModeActive = false;

    function get() {
        // Special handling for catalog_option - fetch from store-catalog microservice
        if (provider === PROVIDERS.STORE && name === 'catalog_option') {
            return storeCatalogOptions();
        }

        // Special handling for catalog_custom_fields - fetch from store-catalog microservice
        if (provider === PROVIDERS.STORE && name === 'catalog_custom_fields') {
            const type = extractCatalogCustomFieldsType(
                flexibleWhereModeActive ? flexibleWhereState : filters
            );
            return storeCatalogCustomFields({ type });
        }

        name =
            provider === PROVIDERS.STORE
                ? `internal-usage#store_catalog#${name}`
                : name;
        return getCollectionData({
            name,
            page,
            filters,
            search: searchString,
            sortBy,
            fields,
            where: flexibleWhereModeActive ? flexibleWhereState : undefined,
        });
    }

    function select(...selected) {
        fields = selected;
        return api;
    }

    function pageNumber(num) {
        if (!page) {
            page = {};
        }
        page.pageNumber = num;
        return api;
    }

    function pageSize(size) {
        if (!page) {
            page = {};
        }
        page.pageSize = size;
        return api;
    }

    function orderBy(field, direction = null) {
        if (!sortBy) {
            sortBy = [];
        }
        sortBy.push({ field, direction });
        return api;
    }

    function where(field, operator, value) {
        const sanitizedValue = sanitizeFilterValue(value);
        if (flexibleWhereModeActive) {
            addFlexiblePredicate(field, operator, sanitizedValue, 'root.where');
            return api;
        }

        if (isCollectionsJSapiFilterValidationEnabled()) {
            const err = validateLegacyFilter({
                field,
                operator,
                value: sanitizedValue,
            });
            if (err) {
                throw new Error(`[Collection API] ${err}`);
            }
        }

        if (!filters) {
            filters = [];
        }
        filters.push({ field, operator, value: sanitizedValue });
        return api;
    }

    function search(string) {
        searchString = sanitizeStringValue(string);
        return api;
    }

    function activateFlexibleMode(invokerName) {
        ensureFlexibleFeatureEnabled(invokerName);
        if (filters && filters.length) {
            throw new Error(
                `[Collection API] Cannot mix legacy filters with flexible where while calling ${invokerName}. Start a new query builder to use grouped conditions.`
            );
        }
        if (!flexibleWhereModeActive) {
            flexibleWhereModeActive = true;
            if (!flexibleWhereState) {
                flexibleWhereState = {
                    op: LOGICAL_OPERATORS.AND,
                    clauses: [],
                };
            }
        }
    }

    function addFlexiblePredicate(field, operator, value, methodName) {
        const clauses = getFlexibleClauses();
        clauses.push(buildPredicateClause(field, operator, value, methodName));
    }

    function getFlexibleClauses() {
        if (!flexibleWhereState) {
            flexibleWhereState = {
                op: LOGICAL_OPERATORS.AND,
                clauses: [],
            };
        } else if (!flexibleWhereState.clauses) {
            flexibleWhereState.clauses = [];
        }
        return flexibleWhereState.clauses;
    }

    // Initialize flexibleWhereState before creating the builder
    // This ensures whereState is defined when createWhereBuilder tries to set properties on it
    if (!flexibleWhereState) {
        flexibleWhereState = {
            op: LOGICAL_OPERATORS.AND,
            clauses: [],
        };
    }

    // Create a shared where builder that both commonQueryBuilder and aggregate use
    const whereBuilder = createWhereBuilder(
        flexibleWhereState,
        activateFlexibleMode,
        addFlexiblePredicate,
        getFlexibleClauses,
        'queryBuilder'
    );

    // Expose where builder methods directly on the API, but return api for chaining
    function root(op = LOGICAL_OPERATORS.AND) {
        whereBuilder.root(op);
        return api;
    }

    function andGroup(callback) {
        whereBuilder.andGroup(callback);
        return api;
    }

    function orGroup(callback) {
        whereBuilder.orGroup(callback);
        return api;
    }

    const api = {
        get,
        pageSize,
        pageNumber,
        orderBy,
        where,
        select,
        search,
        root,
        andGroup,
        orGroup,
    };

    // Attach predicate shortcuts that delegate to the where builder
    attachPredicateShortcuts(
        api,
        (field, operator, value, shortcut) => {
            activateFlexibleMode(shortcut);
            addFlexiblePredicate(field, operator, value, shortcut);
        },
        api
    );

    return api;
}

async function logError({ error, context, response }) {
    const errorData = {
        context,
        url: response?.url,
        status: response?.status,
        'd-id': response?.headers?.get?.('d-request-id'),
    };

    if (shouldSkipErrorLogging(error, response)) {
        return;
    }

    logService.error({
        message: `[Collection API] Error fetching collection data: ${error.message}`,
        siteAlias: window.Parameters.SiteAlias,
        dataString: JSON.stringify(errorData),
        error,
        tags: ['collections-api'],
    });
}

function isJsonResponseCompatible(response) {
    const contentType = response.headers.get('Content-type');
    return contentType?.includes('json');
}

function attachPredicateShortcuts(target, adder, returnTarget) {
    target.eq = (field, value) => {
        adder(field, 'EQ', value, 'eq');
        return returnTarget;
    };
    target.ne = (field, value) => {
        adder(field, 'NE', value, 'ne');
        return returnTarget;
    };
    target.gt = (field, value) => {
        adder(field, 'GT', value, 'gt');
        return returnTarget;
    };
    target.gte = (field, value) => {
        adder(field, 'GTE', value, 'gte');
        return returnTarget;
    };
    target.lt = (field, value) => {
        adder(field, 'LT', value, 'lt');
        return returnTarget;
    };
    target.lte = (field, value) => {
        adder(field, 'LTE', value, 'lte');
        return returnTarget;
    };
    target.in = (field, values) => {
        adder(field, 'IN', values, 'in');
        return returnTarget;
    };
    target.nin = (field, values) => {
        adder(field, 'NIN', values, 'nin');
        return returnTarget;
    };
    target.btwn = (field, min, max) => {
        adder(field, 'BTWN', [min, max], 'btwn');
        return returnTarget;
    };
}

function normalizeLogicalOp(op, methodName) {
    if (!op) {
        return LOGICAL_OPERATORS.AND;
    }
    const normalized = `${op}`.toUpperCase();
    if (!Object.values(LOGICAL_OPERATORS).includes(normalized)) {
        throw new Error(
            `[Collection API] Invalid logical operator '${op}' provided to ${methodName}. Use 'AND' or 'OR'.`
        );
    }
    return normalized;
}

function buildPredicateClause(field, operator, value, methodName) {
    if (field === undefined || field === null) {
        throw new Error(
            `[Collection API] Field name must be provided for ${methodName}.`
        );
    }
    if (operator === undefined || operator === null) {
        throw new Error(
            `[Collection API] Operator must be provided for ${methodName}.`
        );
    }
    if (isCollectionsJSapiFilterValidationEnabled()) {
        const err = validatePredicateClause({
            fieldName: field,
            operator,
            value,
        });
        if (err) {
            throw new Error(`[Collection API] ${err}`);
        }
    }
    const normalizedOperator =
        typeof operator === 'string' ? operator.toUpperCase() : operator;
    return {
        fieldName: field,
        operator: normalizedOperator,
        value: sanitizeFilterValue(value),
    };
}

function ensureFlexibleFeatureEnabled() {
    // Flexible feature is always enabled
}

/**
 * Sanitizes string values by replacing newline characters with spaces.
 * This prevents %0A characters in URLs which nginx doesn't allow.
 */
function sanitizeStringValue(value) {
    if (typeof value === 'string') {
        return value.replace(/\n/g, ' ');
    }
    return value;
}

/**
 * Recursively sanitizes filter values and other string values in query payloads.
 */
function sanitizeFilterValue(value) {
    if (typeof value === 'string') {
        return sanitizeStringValue(value);
    }
    if (Array.isArray(value)) {
        return value.map(sanitizeFilterValue);
    }
    if (value && typeof value === 'object') {
        const sanitized = {};
        for (const [key, val] of Object.entries(value)) {
            sanitized[key] = sanitizeFilterValue(val);
        }
        return sanitized;
    }
    return value;
}

function encodeQueryPayload(payload = {}) {
    const cleanedPayload = Object.entries(payload ?? {}).reduce(
        (acc, [key, value]) => {
            if (value !== undefined) {
                // Sanitize filters, search, and where clauses to replace newlines with spaces
                acc[key] = sanitizeFilterValue(value);
            }
            return acc;
        },
        {}
    );
    return rison.encode(cleanedPayload);
}

function shouldSkipErrorLogging(error, response) {
    const sts = response?.status;
    const msg = error?.message;
    return (
        // Known error, no need to log
        (sts === 400 &&
            msg.includes(
                'The requested site does not belong to this domain'
            )) ||
        // Skip logging 404 errors with "No collection for site alias" message
        (sts === 404 && msg.includes('No collection for site alias')) ||
        // Skip logging 404 errors with non-json content type
        (sts === 404 && !isJsonResponseCompatible(response)) ||
        // Skip logging bingbot errors
        navigator.userAgent.includes('bingbot')
    );
}

/**
 * Fetches catalog options from the store-catalog microservice.
 * This function fetches options data from the store-catalog endpoints instead of the collections microservice.
 * This will be changed in some later future, for now it's just a workaround.
 */
export async function storeCatalogOptions() {
    const path = pathParamsForCurrentServer();
    const url = `/${path.prefix}/store-catalog/public/${path.siteAlias}/options`;

    return await cacheableRequest(url, {
        context: { type: 'storeCatalogOptions' },
    });
}

const CATALOG_CUSTOM_FIELDS_ALLOWED_TYPES = new Set([
    'TEXT',
    'PLAIN_TEXT',
    'IMAGE',
    'VIDEO',
]);

function extractCatalogCustomFieldsType(source) {
    if (!source) {
        return undefined;
    }
    const candidates = Array.isArray(source)
        ? source
        : Array.isArray(source.clauses)
        ? source.clauses
        : [];
    for (const clause of candidates) {
        if (clause?.field === 'type' && clause?.operator === 'eq') {
            return clause.value;
        }
    }
    return undefined;
}

/**
 * Fetches catalog custom fields from the store-catalog microservice.
 * This function fetches custom fields data from the store-catalog endpoints instead of the collections microservice.
 * This will be changed in some later future, for now it's just a workaround.
 */
export async function storeCatalogCustomFields({ type } = {}) {
    const path = pathParamsForCurrentServer();
    const params = CATALOG_CUSTOM_FIELDS_ALLOWED_TYPES.has(type)
        ? `?type=${encodeURIComponent(type)}`
        : '';
    const url = `/${path.prefix}/store-catalog/public/${path.siteAlias}/custom-fields${params}`;

    return await cacheableRequest(url, {
        context: { type: 'storeCatalogCustomFields' },
    });
}
